import { useEffect, lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Footer from "@/components/Footer";
import { Skeleton } from "@/components/ui/skeleton";

// Lazy load below-the-fold sections to reduce main thread work
const ServicesIntro = lazy(() => import("@/components/ServicesIntro"));
const WhyUsSection = lazy(() => import("@/components/WhyUsSection"));
const WhyChooseSection = lazy(() => import("@/components/WhyChooseSection"));
const FeaturedCase = lazy(() => import("@/components/FeaturedCase"));
const FAQSection = lazy(() => import("@/components/FAQSection"));

// Skeleton fallback for lazy-loaded sections to prevent layout shift
const SectionSkeleton = () => (
  <div className="py-16 lg:py-24">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <Skeleton className="h-10 w-64 mx-auto mb-4" />
        <Skeleton className="h-5 w-96 mx-auto" />
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Skeleton className="h-48 rounded-xl" />
        <Skeleton className="h-48 rounded-xl" />
        <Skeleton className="h-48 rounded-xl hidden lg:block" />
      </div>
    </div>
  </div>
);

// FAQ data for structured data (must be outside lazy-loaded component for SEO)
const faqs = [
  {
    question: "Hvad gør jeg, hvis jeg har en skade?",
    answer:
      "Kontakt os med det samme – vi tilbyder en gratis og uforpligtende vurdering af din sag. Vi gennemgår din situation grundigt og giver dig et konkret tilbud. Vi vender altid tilbage samme dag.",
  },
  {
    question: "Hvad koster jeres rådgivning?",
    answer:
      "Første møde er gratis og uforpligtende. Standard timepris er 995 kr. + moms og kørsel. Vi arbejder efter princippet 'no cure – no pay', og tager kun opgaver hvor vi kan skabe værdi for dig.",
  },
  {
    question: "Hvordan foregår et møde med CECCA?",
    answer:
      "Vi starter med at gennemgå din sag grundigt og vurdere, om vi kan hjælpe. Derefter laver vi et transparent tilbud baseret på sagens kompleksitet. Du betaler kun, hvis vi indgår en aftale.",
  },
  {
    question: "Kan I hjælpe med både private og erhvervssager?",
    answer:
      "Ja, vi rådgiver både privatkunder, erhvervskunder og landbrug. Vi fokuserer på større og omfattende skadessager – brandskader, forsikringsopgørelser, ejerskifte, indbo og driftstab.",
  },
  {
    question: "Dækker forsikringen jeres honorar?",
    answer:
      "I mange tilfælde ja. Det afhænger af din police og skadens karakter. Vi hjælper dig med at afklare dette allerede ved første kontakt.",
  },
  {
    question: "Er I landsdækkende?",
    answer:
      "Ja, vi tilbyder rådgivning i hele Danmark, men har lokal forankring i Kolding-området. Vi er tæt på kunden og altid tilgængelige.",
  },
];

const Index = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const elementId = location.hash.replace("#", "");
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }, [location.hash]);

  const organizationData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "CECCA Rådgivning",
    "alternateName": "Cecca",
    "url": "https://cecca.dk",
    "logo": "https://cecca.dk/logo.png",
    "description": "Din uvildige rådgiver ved større skader. Vi hjælper med brandskader, forsikringsopgørelser, ejerskifte, indbo og driftstab – fra start til slut.",
    "foundingDate": "2023",
    "areaServed": {
      "@type": "Country",
      "name": "Danmark"
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Drosselvej 2",
      "addressLocality": "Lunderskov",
      "postalCode": "6640",
      "addressCountry": "DK"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+45 93 88 90 29",
      "email": "cp@cecca.dk",
      "contactType": "customer service",
      "availableLanguage": "Danish"
    },
    "sameAs": [],
    "taxID": "DK44589842"
  };

  const localBusinessData = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "CECCA Rådgivning",
    "image": "https://cecca.dk/logo.png",
    "@id": "https://cecca.dk",
    "url": "https://cecca.dk",
    "telephone": "+45 93 88 90 29",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Drosselvej 2",
      "addressLocality": "Lunderskov",
      "postalCode": "6640",
      "addressCountry": "DK"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 55.4768,
      "longitude": 9.2877
    },
    "areaServed": "Danmark",
    "serviceType": [
      "Brandskader",
      "Forsikringsopgørelser",
      "Ejerskifte",
      "Indbo og driftstab"
    ]
  };

  return (
    <>
      <Helmet>
        <title>CECCA Rådgivning | Uvildig skaderådgivning</title>
        <meta 
          name="description" 
          content="Specialist i større brand-, vand-, storm- og sneskader, forsikringsopgørelser, ejerskifte, indbo, driftstab og byggestyring. No cure – no pay." 
        />
        <meta name="keywords" content="brandskader, vandskader, stormskader, snetrykskader, totalskader, sanering, nedrivning, forsikringsopgørelser, ejerskifteforsikring, indbo, byggestyring, driftstab" />
        <link rel="canonical" href="https://cecca.dk" />
        
        {/* Open Graph */}
        <meta property="og:title" content="CECCA Rådgivning | Uvildig skaderådgivning" />
        <meta property="og:description" content="Vi er din specialist i større brand-, vand- og stormskader, snetrykskader, totalskader, sanering og nedrivning, samt forsikringsopgørelser, ejerskifteforsikring, indbo, byggestyring, og driftstab." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cecca.dk" />
        <meta property="og:image" content="https://cecca.dk/og-logo.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:alt" content="CECCA Rådgivning logo" />
        <meta property="og:locale" content="da_DK" />
        <meta property="og:site_name" content="CECCA Rådgivning" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="CECCA Rådgivning | Uvildig skaderådgivning" />
        <meta name="twitter:description" content="Vi er din specialist i større brand-, vand- og stormskader, snetrykskader, totalskader, sanering og nedrivning, samt forsikringsopgørelser, ejerskifteforsikring, indbo, byggestyring, og driftstab." />
        <meta name="twitter:image" content="https://cecca.dk/og-logo.png" />
        
        {/* Organization Schema */}
        <script type="application/ld+json">
          {JSON.stringify(organizationData)}
        </script>
        
        {/* LocalBusiness Schema */}
        <script type="application/ld+json">
          {JSON.stringify(localBusinessData)}
        </script>
        
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map((faq) => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer,
              },
            })),
          })}
        </script>
      </Helmet>

      <div className="min-h-screen">
        <Header />
        <main>
          <Hero />
          <Suspense fallback={<SectionSkeleton />}>
            <ServicesIntro />
          </Suspense>
          <Suspense fallback={<SectionSkeleton />}>
            <WhyUsSection />
          </Suspense>
          <Suspense fallback={<SectionSkeleton />}>
            <WhyChooseSection />
          </Suspense>
          <Suspense fallback={<SectionSkeleton />}>
            <FeaturedCase />
          </Suspense>
          <Suspense fallback={<SectionSkeleton />}>
            <FAQSection />
          </Suspense>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Index;
